import { AlertTriangle, ChevronLeft, ChevronRight, Download, Loader2 } from 'lucide-react'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Document, Page } from 'react-pdf'

import '@/utils/pdf'

import { Button } from '@/components/elements/button'
import { cn } from '@/utils/helpers'

import Block from './Block'

// A PDF counts as "viewed" per the block's configured completion mode
// (RSCX-373): every page, just the last page, or a percentage of pages. Pages
// are tracked with an IntersectionObserver when scrolling vertically, or on
// render when paging horizontally; the Next button unlocks when the
// requirement is met. Completion itself stays binary (the parent posts
// track_view on advance), so nothing new is persisted per-page — this only
// gates progression client-side.
function BlockPDF({ block, nextBlock, isLastBlock, upNextTitle }) {
  const { content } = block
  const alreadyComplete = ['Completed', 'Quiz Pending'].includes(block.status)

  const completionMode = content.pdfCompletionMode ?? 'All Pages'
  const completionPercent = content.pdfCompletionPercent
  const horizontal = (content.pdfOrientation ?? 'Vertical') === 'Horizontal'

  const [numPages, setNumPages] = useState(null)
  const [viewedPages, setViewedPages] = useState(() => new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const [loadError, setLoadError] = useState(false)
  const [containerEl, setContainerEl] = useState(null)
  const [containerWidth, setContainerWidth] = useState(0)
  // Horizontal mode fits the page to the visible player area instead of the
  // container width, so a portrait page doesn't force vertical scrolling.
  const [pageAreaEl, setPageAreaEl] = useState(null)
  const [pageAreaHeight, setPageAreaHeight] = useState(0)
  const [pageAspect, setPageAspect] = useState(null)

  // pageNumber -> wrapper node, and the reverse (node -> pageNumber) so the
  // observer callback can resolve which page came into view.
  const nodesRef = useRef(new Map())
  const pageNumbersRef = useRef(new Map())
  // Pages whose canvas has painted (eligible for observation) and pages already
  // counted, mirrored outside state so the ref-attach path below can restore
  // observation without depending on render-scoped values.
  const renderedPagesRef = useRef(new Set())
  const countedPagesRef = useRef(new Set())

  const observerRef = useRef(null)
  if (!observerRef.current) {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const pageNumber = pageNumbersRef.current.get(entry.target)
          if (pageNumber == null || countedPagesRef.current.has(pageNumber)) return
          countedPagesRef.current.add(pageNumber)
          setViewedPages((prev) => new Set(prev).add(pageNumber))
          observerRef.current.unobserve(entry.target)
        })
      },
      // Fire as soon as a sliver of the page enters the viewport. A page can be
      // taller than the viewport, so a high threshold might never be met;
      // requiring every page (including the last) to enter still forces the
      // student to scroll all the way through.
      { threshold: 0.1 },
    )
  }

  useEffect(() => {
    const observer = observerRef.current
    return () => observer.disconnect()
  }, [])

  // Scale pages to the container rather than pdf.js's intrinsic size.
  // clientWidth excludes the scrollbar, avoiding horizontal overflow.
  useEffect(() => {
    if (!containerEl) return undefined
    const update = () => setContainerWidth(containerEl.clientWidth)
    update()
    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(containerEl)
    return () => resizeObserver.disconnect()
  }, [containerEl])

  // Horizontal mode: track the height the page area was allotted (its flex
  // share of the player), so the page can be scaled to fit it.
  useEffect(() => {
    if (!pageAreaEl) return undefined
    const update = () => setPageAreaHeight(pageAreaEl.clientHeight)
    update()
    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(pageAreaEl)
    return () => resizeObserver.disconnect()
  }, [pageAreaEl])

  const setPageNode = useCallback((node, pageNumber) => {
    if (node) {
      nodesRef.current.set(pageNumber, node)
      pageNumbersRef.current.set(node, pageNumber)
      // The inline ref on each wrapper is a new function every render, so React
      // detaches (null) and re-attaches all refs on every re-render — including
      // the one caused by the first setViewedPages. The detach path unobserves,
      // so the attach path must restore observation for any page that has
      // already rendered but hasn't been counted, or tracking dies after the
      // first update.
      if (renderedPagesRef.current.has(pageNumber) && !countedPagesRef.current.has(pageNumber)) {
        observerRef.current.observe(node)
      }
    } else {
      // Unobserve and drop both map entries on unmount so a remount (e.g. the
      // signed URL refreshing the Document) can't leave the observer holding
      // detached nodes or resolve a stale node to the wrong page.
      const prev = nodesRef.current.get(pageNumber)
      if (prev) {
        observerRef.current.unobserve(prev)
        pageNumbersRef.current.delete(prev)
        nodesRef.current.delete(pageNumber)
      }
    }
  }, [])

  // Start observing a page only after its canvas has rendered. react-pdf paints
  // pages asynchronously; until then every wrapper is collapsed at the top of
  // the scroll area, and observing them there would report the whole document as
  // "viewed" the instant it loads.
  const observePage = useCallback((pageNumber) => {
    renderedPagesRef.current.add(pageNumber)
    if (countedPagesRef.current.has(pageNumber)) return
    const node = nodesRef.current.get(pageNumber)
    if (!node) return
    pageNumbersRef.current.set(node, pageNumber)
    observerRef.current.observe(node)
  }, [])

  // Horizontal mode shows one page at a time, so rendering IS viewing — no
  // IntersectionObserver involved.
  const markPageViewed = useCallback((pageNumber) => {
    if (countedPagesRef.current.has(pageNumber)) return
    countedPagesRef.current.add(pageNumber)
    setViewedPages((prev) => new Set(prev).add(pageNumber))
  }, [])

  // How many pages (and which) the configured mode requires.
  const requiredPages = (() => {
    if (numPages == null) return null
    if (completionMode === 'Last Page') return 1
    if (completionMode === 'Percent of Pages') {
      return Math.max(1, Math.ceil((numPages * (completionPercent ?? 100)) / 100))
    }
    return numPages
  })()
  const requirementMet = (() => {
    if (numPages == null) return false
    if (completionMode === 'Last Page') return viewedPages.has(numPages)
    return viewedPages.size >= requiredPages
  })()

  // When the viewer fails we can't gate on viewing, so degrade to the old
  // download-and-continue behavior rather than dead-ending the student.
  const canProceed = alreadyComplete || requirementMet || loadError

  const progressHint = (() => {
    if (completionMode === 'Last Page') {
      return horizontal
        ? 'Page through to the last page to continue'
        : 'Scroll to the last page to continue'
    }
    const seen = Math.min(viewedPages.size, requiredPages ?? viewedPages.size)
    const verb = horizontal ? 'Page through' : 'Scroll through'
    if (completionMode === 'Percent of Pages') {
      return `${verb} at least ${requiredPages} pages to continue — viewed ${seen} of ${requiredPages}`
    }
    return `${verb} all pages to continue — viewed ${seen} of ${numPages}`
  })()

  // Fit the horizontal page to the allotted height (with a floor so it stays
  // readable in short windows — the page area scrolls if the floor wins). A
  // landscape page that would overflow the width at that height falls back to
  // width fit instead. react-pdf ignores `height` when `width` is set.
  const MIN_PAGE_HEIGHT = 400
  const horizontalPageSize = (() => {
    if (!pageAreaHeight) return { width: containerWidth || undefined }
    const height = Math.max(MIN_PAGE_HEIGHT, pageAreaHeight)
    if (pageAspect && containerWidth && height * pageAspect > containerWidth) {
      return { width: containerWidth }
    }
    return { height }
  })()

  const renderPage = (pageNumber, { observed }) => (
    <div
      key={pageNumber}
      ref={observed ? (node) => setPageNode(node, pageNumber) : undefined}
      className={cn('border shadow-sm', !horizontal && 'mb-4')}
    >
      <Page
        pageNumber={pageNumber}
        {...(horizontal ? horizontalPageSize : { width: containerWidth || undefined })}
        onLoadSuccess={
          horizontal
            ? (page) => setPageAspect(page.originalWidth / page.originalHeight)
            : undefined
        }
        onRenderSuccess={() => (observed ? observePage(pageNumber) : markPageViewed(pageNumber))}
        // A page that never renders can never be observed, which would leave
        // the block permanently locked with no hint why. A partially-rendered
        // document can't be honestly view-gated anyway, so treat any page
        // failure like a document failure and offer the download fallback.
        onLoadError={() => setLoadError(true)}
        onRenderError={() => setLoadError(true)}
      />
    </div>
  )

  return (
    <Block
      canProceed={canProceed}
      nextBlock={nextBlock}
      isLastBlock={isLastBlock}
      title={content.name ?? 'PDF'}
      block={block}
      upNextTitle={upNextTitle}
    >
      <div
        ref={setContainerEl}
        className={cn('relative flex flex-col items-center gap-4', horizontal && 'h-full')}
      >
        <div className="flex w-full flex-row items-center justify-end">
          {/* Same-tab navigation would tear down the course player, so open the
              signed URL (inline disposition) in a new tab like the documents
              tables do. Available during the course and when reviewing. */}
          <a
            href={content.signedFilePath}
            target="_blank"
            rel="noreferrer"
            className="text-primary hover:text-foreground flex flex-row items-center gap-1 text-sm font-medium"
          >
            <Download className="h-4 w-4" />
            Download PDF
          </a>
        </div>

        {!alreadyComplete && numPages != null && !loadError && (
          <div className="bg-secondary sticky top-0 z-10 w-full py-1 text-center text-xs font-medium">
            {canProceed ? (
              <span className="text-success">Viewing requirement met — you can continue</span>
            ) : (
              <span className="text-muted-foreground">{progressHint}</span>
            )}
          </div>
        )}

        {loadError ? (
          <div className="text-muted-foreground flex flex-col items-center gap-2 p-8 text-center text-sm">
            <AlertTriangle className="text-destructive h-6 w-6" />
            <p>This PDF couldn&apos;t be displayed. Download it to review the material.</p>
            <a href={content.signedFilePath} className="text-primary underline">
              Download the PDF
            </a>
          </div>
        ) : (
          <>
            <div
              ref={horizontal ? setPageAreaEl : undefined}
              className={cn(
                'w-full',
                // min-h-0 lets the flex item shrink to its allotted share; the
                // area scrolls when the min page height exceeds it.
                horizontal && 'flex min-h-0 flex-1 items-start justify-center overflow-auto',
              )}
            >
              <Document
                file={content.signedFilePath}
                onLoadSuccess={({ numPages: count }) => setNumPages(count)}
                onLoadError={() => setLoadError(true)}
                onSourceError={() => setLoadError(true)}
                loading={
                  <div className="grid place-items-center p-8">
                    <Loader2 className="text-primary h-8 w-8 animate-spin" />
                  </div>
                }
              >
                {horizontal
                  ? numPages != null && renderPage(currentPage, { observed: false })
                  : Array.from({ length: numPages ?? 0 }, (_, index) =>
                      renderPage(index + 1, { observed: true }),
                    )}
              </Document>
            </div>

            {horizontal && numPages != null && (
              <div className="flex shrink-0 flex-row items-center gap-4 pb-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-muted-foreground text-sm">
                  Page {currentPage} of {numPages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= numPages}
                  onClick={() => setCurrentPage((page) => Math.min(numPages, page + 1))}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Block>
  )
}

export default BlockPDF

BlockPDF.propTypes = {
  block: PropTypes.object.isRequired,
  nextBlock: PropTypes.func.isRequired,
  isLastBlock: PropTypes.bool,
  upNextTitle: PropTypes.string,
}
